import { Box, Stack } from '@mui/material'
import {
    AppraiserAssignment as AppraiserAssignmentWidget,
    ContentChip,
    DisplayUserLink,
    HeadlineWithSpacing,
    InfoTooltip,
    tokens,
} from 'ComponentLibrary'
import { TinyButton } from 'ComponentLibrary/src/piazza/buttons'
import { PlusIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import type { AppraiserAssignment as AppraiserAssignmentSchema, RootUser as User } from '@einwert/valuation-api-client'
import { ExternalAppraiserModal } from 'ClientPortal/src/pages/Orders/OrderDetails/AppraiserSection/ExternalAppraiserModal'
import { type ExternalParticipant } from '@einwert/valuation-api-client'
import { useDeleteExternalParticipant } from 'ClientPortal/src/api/Queries/orders'
import { StyledUserAssignmentButton } from 'ComponentLibrary/src/widgets/AppraiserAssignment/AppraiserAssignment'
import { ExternalAppraiserDeleteModal } from './ExternalAppraiserDeleteModal'
import { useTranslation } from 'react-i18next'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type AppraiserSectionProps = {
    orderId: string
    appraiserAssignment?: AppraiserAssignmentSchema
    appraiserUsers: User[]
    externalParticipants: ExternalParticipant[]
    hasAsset: boolean
    userLinkCopy?: Partial<UserLinkCopy>
}

export function AppraiserSection({
    orderId,
    appraiserAssignment,
    appraiserUsers,
    externalParticipants,
    hasAsset,
    userLinkCopy,
}: AppraiserSectionProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage.externalAppraiser' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const { isExternalParticipantsEnabled } = useFlags()
    const [isExternalAppraiserModalOpen, setIsExternalAppraiserModalOpen] = useState(false)
    const [externalParticipantToDelete, setExternalParticipantToDelete] = useState<ExternalParticipant>()
    const hasExternalParticipants = externalParticipants.length > 0
    const closeDeleteModal = () => setExternalParticipantToDelete(undefined)
    const { deleteExternalParticipant, isDeleteExternalParticipantPending } = useDeleteExternalParticipant({
        orderId,
        onSuccess: closeDeleteModal,
    })

    const handleCloseDeleteModal = () => {
        if (!isDeleteExternalParticipantPending) {
            closeDeleteModal()
        }
    }

    const handleDeleteExternalParticipant = () => {
        if (!externalParticipantToDelete) {
            return
        }

        deleteExternalParticipant({ participantId: externalParticipantToDelete.uuid })
    }

    const handleAddExternalParticipant = () => {
        setIsExternalAppraiserModalOpen(true)
    }

    return (
        <>
            <HeadlineWithSpacing
                variant="headline2"
                title={tTerminology('appraiser')}
                endAdornment={
                    isExternalParticipantsEnabled && (
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing050}>
                            <TinyButton
                                label={t('addExternal')}
                                leftIcon={<PlusIcon />}
                                onClick={handleAddExternalParticipant}
                                disabled={hasExternalParticipants}
                                tooltip={hasExternalParticipants ? t('maxOneTooltip') : undefined}
                            />
                            <ContentChip size="small" color="purple" label={t('beta')} />
                            <InfoTooltip text={t('infoTooltip')} size={tokens.SizingIconS} style={{ color: tokens.ColorIconSubtle }} />
                        </Stack>
                    )
                }
            />
            {appraiserAssignment || hasExternalParticipants ? (
                <Stack direction="column" gap={tokens.SizingSpacing050}>
                    {appraiserAssignment && (
                        <AppraiserAssignmentWidget
                            appraiserAssignment={appraiserAssignment}
                            users={appraiserUsers}
                            size="medium"
                            userLinkCopy={userLinkCopy}
                        />
                    )}
                    {hasExternalParticipants && (
                        <Stack direction="column" gap={tokens.SizingSpacing100}>
                            {externalParticipants.map((externalParticipant) => (
                                <StyledUserAssignmentButton
                                    key={externalParticipant.uuid}
                                    onClick={() => setExternalParticipantToDelete(externalParticipant)}
                                    aria-label={t('removeAriaLabel', { email: externalParticipant.email })}
                                >
                                    <DisplayUserLink
                                        key={externalParticipant.uuid}
                                        size="medium"
                                        user={externalParticipant}
                                        isExternal
                                        copy={userLinkCopy}
                                    />
                                </StyledUserAssignmentButton>
                            ))}
                        </Stack>
                    )}
                </Stack>
            ) : (
                <Box padding={tokens.SizingSpacing100} alignItems="center" display="flex">
                    <DisplayUserLink size="medium" copy={userLinkCopy} />
                </Box>
            )}
            <ExternalAppraiserModal
                isOpen={isExternalAppraiserModalOpen}
                onClose={() => setIsExternalAppraiserModalOpen(false)}
                orderId={orderId}
                hasAsset={hasAsset}
            />
            <ExternalAppraiserDeleteModal
                isOpen={!!externalParticipantToDelete}
                onClose={handleCloseDeleteModal}
                externalParticipant={externalParticipantToDelete}
                isPending={isDeleteExternalParticipantPending}
                onDelete={handleDeleteExternalParticipant}
            />
        </>
    )
}
